import { api } from '../api';

export type LegajoCategoria = 'ok' | 'observaciones' | 'preexistencias';
export type LegajoEstadoVista = 'pendientes' | 'en_alta_binary' | 'dado_de_alta';

export interface CategoriaCount {
  legajos: number;
  personas: number;
}

export interface CountsPorEstado {
  ok: CategoriaCount;
  observaciones: CategoriaCount;
  preexistencias: CategoriaCount;
  /**
   * Legajos con el TITULAR bloqueado en el paso pendientes: el export los
   * filtra, así que NO están en ok/observaciones/preexistencias. Se muestran
   * como aviso ("frenados por bloqueo") para no prometer exportar de más.
   */
  frenados: CategoriaCount;
}

export interface LegajosListosCounts {
  pendientes: CountsPorEstado;
  enAltaBinary: CountsPorEstado;
  dadoDeAlta: CountsPorEstado;
}

export interface LegajoListoItem {
  id: string;
  numero: string;
  empresaId: string;
  padronId: string;
  titularAsociadoId: string;
  familiaresAsociadoIds: string[];
  estado: string;
  activacionCompletadaAt?: string;
  observacionesPendientes: number;
  tienePreexistencias: boolean;
}

export interface LegajosListosPage {
  items: LegajoListoItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DescargaAltaResultado {
  filename: string;
  batchId?: string;
  cantidadLegajos: number;
  categoria: LegajoCategoria;
}

function triggerDownload(data: ArrayBuffer, filename: string): void {
  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function parseFilename(cd: string | undefined, fallback: string): string {
  const m = (cd ?? '').match(/filename="?([^"]+)"?/i);
  return m?.[1] ?? fallback;
}

/**
 * Devuelve los 6 contadores que pinta la vista. Refresca rápido sin traer
 * la lista completa de legajos.
 */
export async function getLegajosListosCounts(): Promise<LegajosListosCounts> {
  const { data } = await api.get<LegajosListosCounts>('/admin/altas-masivas/counts');
  return data;
}

/**
 * Lista paginada de una categoría + estado. Se llama cuando el admin abre el
 * drawer de detalle.
 */
export async function listarLegajosPorCategoria(
  categoria: LegajoCategoria,
  estado: LegajoEstadoVista,
  page = 1,
  pageSize = 50,
): Promise<LegajosListosPage> {
  const { data } = await api.get<LegajosListosPage>('/admin/altas-masivas/legajos', {
    params: { categoria, estado, page, pageSize },
  });
  return data;
}

/**
 * Dispara la descarga del xlsx para una categoría. En la misma request el
 * backend mueve los legajos a `en_alta_binary` y los agrupa en un batch.
 */
export async function descargarAltaMasivaBlob(
  categoria: LegajoCategoria,
): Promise<DescargaAltaResultado> {
  const res = await api.get('/admin/altas-masivas/exportar', {
    responseType: 'blob',
    params: { categoria },
  });
  const filename = parseFilename(
    res.headers['content-disposition'] as string | undefined,
    `alta_masiva_${categoria}.xlsx`,
  );
  const batchId = (res.headers['x-alta-batch-id'] as string | undefined) ?? undefined;
  const cantidadLegajos = Number(res.headers['x-alta-batch-cantidad'] ?? '0') || 0;
  triggerDownload(res.data, filename);
  return { filename, batchId, cantidadLegajos, categoria };
}

export interface DescargaObservacionesResultado {
  filename: string;
  totalLegajos: number;
  totalObservaciones: number;
}

/**
 * Descarga xlsx informativo de TODAS las observaciones pendientes en el
 * sistema. NO modifica estados. Disponible para Medicus (incluye user-medicus
 * de solo lectura) — sirve para procesar correcciones offline.
 */
export async function descargarObservacionesBlob(): Promise<DescargaObservacionesResultado> {
  const res = await api.get('/admin/altas-masivas/observaciones/exportar', {
    responseType: 'blob',
  });
  const filename = parseFilename(
    res.headers['content-disposition'] as string | undefined,
    'observaciones.xlsx',
  );
  const totalLegajos = Number(res.headers['x-observaciones-total-legajos'] ?? '0') || 0;
  const totalObservaciones = Number(res.headers['x-observaciones-total'] ?? '0') || 0;
  triggerDownload(res.data, filename);
  return { filename, totalLegajos, totalObservaciones };
}

// ---------------------------------------------------------------------------
// Alta Grupos — envío a Binary por API (reemplaza la descarga de Excel).
// ---------------------------------------------------------------------------

/** Resultado del envío de UN grupo (legajo) dentro de un job/individual. */
export interface EnvioResultadoGrupo {
  legajo: string;
  titular: { apellido: string; nombres: string; documento: string };
  estado: 'ok' | 'error' | 'omitido';
  legajoId?: string;
  medicards?: Array<{ parentesco: string; documento: string; medicard: string | null }>;
  error?: string;
}

/** Resumen del job de envío en lote (progreso + resultados). */
export interface EnvioLoteResumen {
  jobId: string;
  estado: 'corriendo' | 'terminado' | 'error';
  totalGrupos: number;
  procesados: number;
  exitosos: number;
  fallidos: number;
  omitidos: number;
  nombre?: string;
  startedAt?: string;
  finishedAt?: string;
  error?: string;
  resultados?: EnvioResultadoGrupo[];
}

/** Resultado del envío individual de un legajo (Alta Grupos). */
export interface EnvioLegajoResultado {
  legajoId: string;
  numero?: string;
  titular?: { apellido: string; nombres: string; documento: string };
  estado: 'dado_de_alta' | 'rechazado_binary' | 'omitido' | 'error';
  errores?: string[];
  binaryError?: string;
  integrantes?: Array<{ asociadoId: string; parentesco: string; medicard?: string; afiId?: string }>;
}

/**
 * Envía a Binary por API un lote de legajos listos ("Alta Grupos"): por
 * categoría (ok|preexistencias) o por una lista de legajoIds. Devuelve el jobId
 * para hacer polling con `getEnvioAltaGruposJob`.
 */
export async function enviarAltaGruposLote(opts: {
  categoria?: Extract<LegajoCategoria, 'ok' | 'preexistencias'>;
  legajoIds?: string[];
}): Promise<EnvioLoteResumen> {
  const { data } = await api.post<EnvioLoteResumen>('/admin/altas-masivas/enviar', opts);
  return data;
}

/** Envía a Binary un único legajo (Alta Grupos). */
export async function enviarAltaGrupoLegajo(legajoId: string): Promise<EnvioLegajoResultado> {
  const { data } = await api.post<EnvioLegajoResultado>(
    `/admin/altas-masivas/legajos/${legajoId}/enviar`,
  );
  return data;
}

/** Progreso + resultados de un job de envío "Alta Grupos". */
export async function getEnvioAltaGruposJob(jobId: string): Promise<EnvioLoteResumen> {
  const { data } = await api.get<EnvioLoteResumen>(`/admin/altas-masivas/enviar/${jobId}`);
  return data;
}

// ---------------------------------------------------------------------------
// Bandeja de rechazados + reconciliación contra Binary.
// ---------------------------------------------------------------------------

export interface RechazadoIntegrante {
  asociadoId: string;
  parentesco: string;
  apellSolt: string;
  nombres: string;
  documento: string;
  cuil?: string;
}

export interface RechazadoItem {
  id: string;
  numero: string;
  empresaId: string;
  padronId: string;
  binaryError?: string;
  binaryAt?: string;
  titular: RechazadoIntegrante & { cp?: string };
  integrantes: RechazadoIntegrante[];
}

export interface RechazadosPage {
  items: RechazadoItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ReconciliacionResultado {
  revisados: number;
  reconciliados: number;
  detalle: Array<{ numero: string; resultado: string }>;
}

/** Bandeja de legajos rechazados por Binary (motivo ORA + titular + integrantes). */
export async function listarRechazados(page = 1, pageSize = 50, q?: string): Promise<RechazadosPage> {
  const { data } = await api.get<RechazadosPage>('/admin/altas-masivas/rechazados', {
    params: { page, pageSize, ...(q?.trim() ? { q: q.trim() } : {}) },
  });
  return data;
}

/** Reconcilia contra PMED los rechazados por "cuil ya existe/vigente". */
export async function reconciliarRechazados(): Promise<ReconciliacionResultado> {
  const { data } = await api.post<ReconciliacionResultado>('/admin/altas-masivas/reconciliar-rechazados');
  return data;
}

export interface DescargaPreexistenciasResultado {
  filename: string;
  totalLegajos: number;
  totalPreexistencias: number;
}

/**
 * Descarga xlsx con todas las preexistencias declaradas en cuestionarios de
 * salud completados. NO modifica estados. Disponible para Medicus (incluye
 * user-medicus).
 */
export async function descargarPreexistenciasBlob(): Promise<DescargaPreexistenciasResultado> {
  const res = await api.get('/admin/altas-masivas/preexistencias/exportar', {
    responseType: 'blob',
  });
  const filename = parseFilename(
    res.headers['content-disposition'] as string | undefined,
    'preexistencias.xlsx',
  );
  const totalLegajos = Number(res.headers['x-preexistencias-total-legajos'] ?? '0') || 0;
  const totalPreexistencias = Number(res.headers['x-preexistencias-total'] ?? '0') || 0;
  triggerDownload(res.data, filename);
  return { filename, totalLegajos, totalPreexistencias };
}

// ---------- Últimas altas (historial de lo despachado a Binary por API) ----------

export interface UltimaAltaGrupo {
  /** Estado que hoy tiene en Binary: 'A' activo · 'B' baja · null si no se pudo leer. */
  estadoBinary?: string | null;
  /** Plan vigente del grupo en Binary (el familiar hereda el del titular). */
  planActual?: string | null;
  legajoId: string;
  numero: string;
  altaAt: string | null;
  titular: string;
  documento: string;
  personas: number;
  medicard: string | null;
}

export interface PaginaUltimasAltas<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** Últimas altas de GRUPO (Alta Grupos), más recientes primero. */
export async function getUltimasAltasGrupos(
  page: number,
  pageSize: number,
): Promise<PaginaUltimasAltas<UltimaAltaGrupo>> {
  const { data } = await api.get<PaginaUltimasAltas<UltimaAltaGrupo>>(
    '/admin/altas-masivas/ultimas-altas',
    { params: { page, pageSize } },
  );
  return data;
}
